import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import { Modal, Field, Alert, Spinner, CopyButton } from './ui'

interface WhatsAppKit {
  message: string
  phone: { raw: string; e164: string; display: string; ok: boolean; reason?: string }
  url: string
  hasRecipient: boolean
  templateKey: string
}

const TEMPLATES = [
  { key: 'whatsapp_invoice', label: 'New invoice' },
  { key: 'whatsapp_reminder', label: 'Payment reminder' },
]

/**
 * WhatsApp click-to-chat.
 *
 * Opens WhatsApp with the message pre-typed; the send is still a human action.
 * Nothing is transmitted from the server, and no attachment is possible — the
 * message carries the public invoice link, which is where the UPI pay buttons
 * live.
 */
export default function SendWhatsApp({
  invoiceId,
  invoiceNumber,
  onClose,
  onSent,
}: {
  invoiceId: string
  invoiceNumber: string
  onClose: () => void
  onSent: (message: string) => void
}) {
  const [kit, setKit] = useState<WhatsAppKit | null>(null)
  const [templateKey, setTemplateKey] = useState('whatsapp_invoice')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [touchedMessage, setTouchedMessage] = useState(false)

  const load = async (tpl: string, phoneOverride?: string) => {
    setError('')
    try {
      const q = new URLSearchParams({ template: tpl })
      if (phoneOverride) q.set('phone', phoneOverride)
      const k = await api.get<WhatsAppKit>(`/invoices/${invoiceId}/whatsapp?${q}`)
      setKit(k)
      if (!touchedMessage) setMessage(k.message)
      if (phoneOverride === undefined) setPhone(k.phone.raw)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not prepare the message')
    }
  }

  useEffect(() => {
    load(templateKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey])

  // Rebuild the wa.me link client-side so edits to the phone or the message are
  // reflected without another round trip.
  const digits = phone.replace(/\D/g, '')
  const e164 =
    phone.trim().startsWith('+')
      ? digits
      : digits.length === 10
        ? '91' + digits
        : digits.length === 11 && digits.startsWith('0')
          ? '91' + digits.slice(1)
          : digits
  const valid = e164.length >= 10 && e164.length <= 15

  const waUrl = valid
    ? `https://wa.me/${e164}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`

  return (
    <Modal
      wide
      title={`Send invoice ${invoiceNumber} on WhatsApp`}
      onClose={onClose}
      footer={
        <>
          <span className="small muted" style={{ marginRight: 'auto' }}>
            {valid ? `Opens a chat with +${e164}` : 'No valid number — WhatsApp will ask who to send to'}
          </span>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <a
            className="btn primary"
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              onSent(
                'WhatsApp opened with the message ready. Press send there — nothing has been sent yet.',
              )
              onClose()
            }}
          >
            Open WhatsApp
          </a>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      <Alert kind="info">
        This opens WhatsApp with the message typed in — <strong>you press send</strong>. WhatsApp
        click-to-chat cannot attach a PDF, so the message carries the invoice link. Your client can
        view the invoice and pay by UPI from that page.
      </Alert>

      {!kit ? (
        <Spinner label="Preparing…" />
      ) : (
        <>
          <div className="grid c2">
            <Field
              label="Template"
              hint="Editable in Settings → Templates."
            >
              <select value={templateKey} onChange={(e) => { setTouchedMessage(false); setTemplateKey(e.target.value) }}>
                {TEMPLATES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="WhatsApp number"
              hint={
                valid
                  ? `Will open +${e164}. A 10-digit number gets +91 automatically.`
                  : 'Enter 10 digits, or the full number with a + and country code.'
              }
              error={!valid && phone.trim() ? 'This does not look like a valid number' : undefined}
            >
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9412211222 or +44 7700 900000"
              />
            </Field>
          </div>

          <Field label="Message" hint="*Bold* works in WhatsApp. Edits here are used as typed.">
            <textarea
              rows={14}
              value={message}
              onChange={(e) => { setMessage(e.target.value); setTouchedMessage(true) }}
            />
          </Field>

          <div className="btn-row">
            <CopyButton text={message} label="Copy message" />
            {touchedMessage && (
              <button
                className="btn sm"
                onClick={() => { setTouchedMessage(false); load(templateKey) }}
              >
                Reset to template
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}
